import { AppointmentTypes, AvailabilityTypes } from 'common';
import {
  FirstTimeLessonAppointmentAvailability,
  LessonAppointmentAvailability,
} from 'common/dist/infrastructure/modules/appointment/interfaces/AvailabilityTypes';
import { UserSummary } from 'common/dist/infrastructure/modules/auth/interfaces/UserTypes';
import { useInjection } from 'inversify-react';
import { BookingWizardFlowTypes } from 'common/infrastructure/modules/appointment/interfaces/AppointmentTypes';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';

import { WizardStep } from 'domain/entities/WizardStep';
import { AsyncHookResult, InjectableHook, useHookInjection } from 'domain/hooks';
import { IButtonGroupButton } from 'infrastructure/components/ButtonsGroup';
import { Region, RegionType } from 'infrastructure/targets/web/modules/common/helpers';
import { useAppSelector } from 'infrastructure/redux/store/hooks';
import { selectHistory } from 'infrastructure/redux/slices/navigationHistory.selector';

import {
  BookingWizardNavigationHook,
  IBookingWizardNavigationHook,
} from './useCaseNavigateBookingWizard';

export enum ChooseATimeError {
  Unknown = 'unknown',
}

export enum AppointmentPricingError {
  Unknown = 'unknown',
}

export enum AppointmentError {
  Unknown = 'unknown',
}

export const ChooseATimeAdapter = Symbol('ChooseATimeAdapter');

export type IChooseATimeAdapter = InjectableHook<
  AsyncHookResult<Array<FirstTimeLessonAppointmentAvailability>, ChooseATimeError> & {
    selectedInstructor: Partial<LessonAppointmentAvailability> | undefined;
    isFirstTimeLesson: boolean;
    availableFirstTimeLessonSlots: Array<IButtonGroupButton> | undefined;
    lessonTypes: Array<IButtonGroupButton> | undefined;
    scheduledLessonTypes: Array<IButtonGroupButton> | undefined;
    getPricing: (values: ChooseATimeForm) => void;
    getAvailableLessonSlotButtons: (
      selectedLessonLength: number,
    ) => Array<IButtonGroupButton> | undefined;
    setFirstTimeLessonAvailabilitiesStartDate: (date: Date) => void;
    setFirstTimeLessonAvailabilitiesEndDate: (date: Date) => void;
    setStaffAvailabilitiesStartDate: (date: Date) => void;
    setStaffAvailabilitiesEndDate: (date: Date) => void;
    saveAppointmentToStore: (values: ChooseATimeForm) => void;
    areAppointmentsLoading: boolean;
    appointmentError: AppointmentError | undefined;
    pricingError: AppointmentPricingError | undefined;
    appointentPricing: AppointmentTypes.ServicePricing[] | undefined;
    isPricingLoading: boolean;
    userData: UserSummary;
    handleSaveReservationDateToStore: (reservationDate: Date) => void;
    clubFittingTypes: AvailabilityTypes.ClubFittingType[] | undefined;
    clubFittingTypesInProgress: boolean;
    clubFittingTypesError: FetchBaseQueryError | SerializedError | undefined;
    checkIfAtLeastOneSlotAvailable: (date: Date | null) => boolean;
    staffAvailabilities: AvailabilityTypes.StaffAppointmentAvailability[] | undefined;
    selectedBookingType: string | undefined;
  }
>;

export interface ChooseATimeForm {
  location: string;
  reservationDate: string | Date | undefined;
  sessionHour: string;
  lessonLength: number | undefined;
  sessionTypeId: number | undefined;
  resourceId: number | undefined;
  staffId: number | undefined;
}

const useCaseChooseATimeForLesson = () => {
  const bookingWizardNavigation = useHookInjection<IBookingWizardNavigationHook>(
    BookingWizardNavigationHook,
  );
  const adapter = useInjection<IChooseATimeAdapter>(ChooseATimeAdapter);
  const {
    error,
    result,
    inProgress,
    selectedInstructor,
    lessonTypes,
    scheduledLessonTypes,
    isFirstTimeLesson,
    availableFirstTimeLessonSlots,
    setStaffAvailabilitiesStartDate,
    setStaffAvailabilitiesEndDate,
    getAvailableLessonSlotButtons,
    saveAppointmentToStore,
    pricingError,
    appointentPricing,
    isPricingLoading,
    getPricing,
    areAppointmentsLoading,
    appointmentError,
    userData,
    clubFittingTypes,
    clubFittingTypesInProgress,
    clubFittingTypesError,
    selectedBookingType,
    checkIfAtLeastOneSlotAvailable,
    staffAvailabilities,
  } = adapter();
  const isAustralia = Region === RegionType.Australia;
  const isUAE = Region === RegionType.UAE;
  const navigationHistory = useAppSelector(selectHistory);
  const previousStep = navigationHistory[navigationHistory.length - 2];

  const handleNextStep = (values: ChooseATimeForm) => {
    saveAppointmentToStore(values);
    if (!userData.creditCard) {
      bookingWizardNavigation.redirectToStep(WizardStep.paymentDetails);
    } else {
      bookingWizardNavigation.redirectToStep(WizardStep.confirmReservation);
    }
  };

  const handlePreviousStep = () => {
    if (selectedBookingType === BookingWizardFlowTypes.ClubFittingbyCoach) {
      bookingWizardNavigation.redirectToStep(WizardStep.selectFittingType);
    } else if (selectedBookingType === BookingWizardFlowTypes.ClubFitting) {
      if (isUAE || isAustralia) {
        bookingWizardNavigation.redirectToStep(WizardStep.selectInstructor);
      } else {
        bookingWizardNavigation.redirectToStep(previousStep);
      }
    } else {
      bookingWizardNavigation.redirectToStep(WizardStep.selectInstructor);
    }
  };

  return {
    error,
    result,
    inProgress,
    selectedInstructor,
    availableFirstTimeLessonSlots,
    setStaffAvailabilitiesStartDate,
    setStaffAvailabilitiesEndDate,
    getAvailableLessonSlotButtons,
    areAppointmentsLoading,
    appointmentError,
    lessonTypes,
    scheduledLessonTypes,
    pricingError,
    appointentPricing,
    isPricingLoading,
    getPricing,
    nextStep: handleNextStep,
    prevStep: handlePreviousStep,
    isFirstTimeLesson,
    clubFittingTypes,
    clubFittingTypesInProgress,
    clubFittingTypesError,
    checkIfAtLeastOneSlotAvailable,
    staffAvailabilities,
  };
};

export default useCaseChooseATimeForLesson;
